import { FC, lazy, ReactElement, ReactNode, Suspense, useRef } from 'react'

import { Link as RouterLink } from 'react-router-dom'

import {
  NavigationSlates,
  NetworkVisibility,
  RoleType,
} from '@tribeplatform/gql-client/types'
import { useAuthToken } from '@tribeplatform/react-sdk/hooks'
import {
  SlateRenderer,
  SlateRendererProps,
} from '@tribeplatform/react-slate-kit/components'
import {
  Frame,
  FrameProps,
  ResponsiveLogo,
} from '@tribeplatform/react-ui-kit/Frame'
import { useDocument } from '@tribeplatform/react-ui-kit/hooks'

import { useQuery } from '../../hooks/useQuery.js'
import { useMobileSidebar } from '../../MobileSidebarProvider.js'
import { CookieContextProvider } from '../Apps/CookieConsent/index.js'
import { ErrorBoundary } from '../Error/ErrorBoundry.js'
import { Logo } from '../Logo.js'
import { NavbarNavigation } from '../Navbar/navigations/NavbarNavigation.js'
import { SquareLogo } from '../SquareLogo.js'
import { getLayout, layoutsAdditionalContext } from './constants.js'
import { SlateLayout } from './layout.enum.js'
import { SidebarFooter } from './SidebarFooter.js'

const SampleContent = lazy(
  () => import('./sample-components/SampleContent.component.js'),
)

const SampleHeader = lazy(
  () => import('./sample-components/SampleHeader.component.js'),
)

const SampleSidebar = lazy(
  () => import('./sample-components/SampleSidebar.component.js'),
)

const LayoutQueryMap = {
  'no-sidebar': SlateLayout.CLASSIC,
  default: SlateLayout.DEFAULT,
  basic: SlateLayout.BASIC,
  'no-header': SlateLayout.NO_HEADER,
}

type LayoutProps = {
  children: ReactNode
  navigationSlates: NavigationSlates
  context?: SlateRendererProps['context']
  navigations?: {
    Header: ReactElement
    Sidebar1: ReactElement
    Sidebar2: ReactElement
    Footer: ReactElement
  }
  layout: SlateLayout
  hideContent?: boolean
  hideNavigation?: boolean
  sticky?: FrameProps['sticky']
}

export const Layout: FC<LayoutProps> = ({
  layout,
  context,
  navigationSlates,
  navigations,
  hideContent,
  hideNavigation,
  sticky = 'top-bar-navigation',
  children,
}) => {
  const { document } = useDocument()
  const initialLayoutQuery = useRef<keyof typeof LayoutQueryMap>()
  const { mobileSidebarOpen, closeMobileSidebar, navigationItems } =
    useMobileSidebar()

  const {
    header,
    sidebar1,
    sidebar2,
    footer,
    headerDisabled,
    sidebar1Disabled,
  } = navigationSlates || {}

  const queryParams = useQuery()

  if (!initialLayoutQuery.current && queryParams.layout) {
    initialLayoutQuery.current = queryParams.layout as any
  }

  const queryLayoutSlug =
    LayoutQueryMap[initialLayoutQuery.current || queryParams?.layout]

  const layoutSlug = getLayout({
    layout: (queryLayoutSlug || layout) as SlateLayout,
    headerDisabled,
    sidebar1Disabled,
  })
  const extraContext = {
    slate: layoutsAdditionalContext[layoutSlug] || {},
    blocks: {},
  }
  const { Header, Sidebar1 } = navigations || {
    Header: (
      <SlateRenderer
        slate={header}
        context={context}
        extraContext={extraContext}
      />
    ),
    Sidebar1: (
      <SlateRenderer
        slate={sidebar1}
        context={context}
        extraContext={extraContext}
      />
    ),
    Sidebar2: (
      <SlateRenderer
        slate={sidebar2}
        context={context}
        extraContext={extraContext}
      />
    ),
    Footer: (
      <SlateRenderer
        slate={footer}
        context={context}
        extraContext={extraContext}
      />
    ),
  }

  const { data: authToken } = useAuthToken()
  const { networkPublicInfo } = authToken || {}

  const guardNavigation =
    networkPublicInfo?.visibility === NetworkVisibility.private &&
    (!authToken?.member?.role?.type ||
      authToken?.member?.role?.type === RoleType.guest)

  const logo: ResponsiveLogo = {
    plain: (
      <RouterLink to={guardNavigation ? '#' : '/'} className="block logo">
        <Logo image={networkPublicInfo?.logo} alt={networkPublicInfo?.name} />
      </RouterLink>
    ),
    square: (
      <RouterLink
        to={guardNavigation ? '#' : '/'}
        className="block logo-square"
      >
        <SquareLogo
          image={networkPublicInfo?.favicon}
          alt={networkPublicInfo?.name}
          size="sm"
        />
      </RouterLink>
    ),
  }

  const topBar = (
    <ErrorBoundary>
      <Suspense fallback={<></>}>
        {hideNavigation ? <SampleHeader /> : Header}
      </Suspense>
    </ErrorBoundary>
  )

  const navigation = (
    <ErrorBoundary>
      <Suspense fallback={<></>}>
        {hideNavigation ? (
          <SampleSidebar />
        ) : (
          <>
            {Sidebar1}
            <CookieContextProvider>
              <SidebarFooter />
            </CookieContextProvider>
          </>
        )}
      </Suspense>
    </ErrorBoundary>
  )

  const navigationSecondaryMarkup = !guardNavigation &&
    navigationItems?.length && (
      <NavbarNavigation navigationItems={navigationItems} />
    )

  let frameLayout
  switch (layoutSlug) {
    case SlateLayout.CLASSIC:
      frameLayout = 'no-sidebar'
      break
    case SlateLayout.BASIC:
      frameLayout = 'basic'
      break
    case SlateLayout.NO_HEADER:
      frameLayout = 'no-header'
      break
    case SlateLayout.DEFAULT:
    default:
      frameLayout = 'default'
      break
  }

  let fullWidth
  switch (queryLayoutSlug) {
    case SlateLayout.CLASSIC:
    case SlateLayout.BASIC:
      fullWidth = true
      break
    case SlateLayout.NO_HEADER:
    case SlateLayout.DEFAULT:
    default:
      fullWidth = false
      break
  }

  return (
    <Frame
      document={document}
      logo={logo}
      topBar={topBar}
      navigation={navigation}
      navigationSecondary={navigationSecondaryMarkup}
      mobileSidebarOpen={mobileSidebarOpen}
      onNavigationDismiss={closeMobileSidebar}
      layout={frameLayout}
      fullWidth={fullWidth}
      sticky={sticky}
    >
      <ErrorBoundary>
        <Suspense fallback={<></>}>
          {hideContent ? <SampleContent /> : children}
        </Suspense>
      </ErrorBoundary>
    </Frame>
  )
}
